import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowRight, ArrowUpRight, Clock, Gauge, TrendingUp, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { FRICTION_DIMENSIONS } from '../data/mock';

const WelcomeScreen = ({ onStart, onAdmin, language = 'fr' }) => {
  const [showIntro, setShowIntro] = useState(false);

  // Mini Radar Preview Component - Plus grand
  const RadarPreview = () => {
    return (
      <div className="relative w-52 h-52 mx-auto">
        {/* Pentagon background */}
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Grid lines */}
          <polygon 
            points="50,8 92,33 80,88 20,88 8,33" 
            fill="none" 
            stroke="#e5e7eb" 
            strokeWidth="0.5"
          />
          <polygon 
            points="50,22 76,40 68,78 32,78 24,40" 
            fill="none" 
            stroke="#e5e7eb" 
            strokeWidth="0.5"
          />
          <polygon 
            points="50,36 62,46 58,66 42,66 38,46" 
            fill="none" 
            stroke="#e5e7eb" 
            strokeWidth="0.5"
          />
          
          {/* Example data shape */}
          <polygon 
            points="50,18 82,36 72,80 26,74 16,36" 
            fill="rgba(82, 163, 133, 0.25)" 
            stroke="#52a385" 
            strokeWidth="2"
          />
          
          {/* Center point */}
          <circle cx="50" cy="50" r="2" fill="#0f1c47" />
        </svg>
        
        {/* Labels */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] text-gray-600 font-semibold">Priorités</div>
        <div className="absolute top-7 -right-3 text-[10px] text-gray-600 font-semibold">Décision</div>
        <div className="absolute bottom-1 -right-8 text-[10px] text-gray-600 font-semibold">Responsabilisation</div>
        <div className="absolute bottom-1 -left-6 text-[10px] text-gray-600 font-semibold">Communication</div>
        <div className="absolute top-7 -left-3 text-[10px] text-gray-600 font-semibold">Exécution</div>
      </div>
    );
  };

  // Landing Page - Optimisée
  if (!showIntro) {
    return (
      <div className="min-h-screen bg-[#0f1c47] flex items-center justify-center p-4">
        <div className="max-w-lg mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_manager-inventory/artifacts/kqdhn43c_2024_10_make_it_matter.png" 
              alt="WEDO" 
              className="mx-auto h-14 object-contain"
            />
          </div>

          <Card className="bg-white border-0 shadow-2xl">
            <CardContent className="p-8">
              
              {/* TITRE - Raccourci et plus direct */}
              <div className="text-center mb-5">
                <h1 className="text-2xl md:text-[28px] font-bold text-[#0f1c47] leading-tight">
                  Votre équipe avance…
                </h1>
                <h1 className="text-2xl md:text-[28px] font-bold text-[#0f1c47] leading-tight">
                  mais trop de sujets reviennent vers vous ?
                </h1>
              </div>
              
              {/* SOUS-TITRE */}
              <p className="text-center text-[#e62c86] font-medium mb-8">
                En 3 minutes, identifiez les frictions invisibles qui ralentissent votre équipe ou votre organisation.
              </p>

              {/* BLOC PROBLÈME - Liste simple avec icônes */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6">
                <p className="text-gray-700 font-semibold mb-4 text-center">
                  Vous reconnaîtrez probablement ces signaux :
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <ArrowUpRight className="w-5 h-5 text-[#e62c86] flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium whitespace-nowrap">Décisions qui remontent</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <Clock className="w-5 h-5 text-[#e62c86] flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium whitespace-nowrap">Projets ralentis</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <AlertCircle className="w-5 h-5 text-[#e62c86] flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium whitespace-nowrap">Priorités floues</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                    <AlertTriangle className="w-5 h-5 text-[#e62c86] flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium whitespace-nowrap">Tensions implicites</span>
                  </div>
                </div>
              </div>

              {/* PHRASE MIROIR - Plus visible */}
              <p className="text-center text-[#0f1c47] text-xl font-bold italic mb-6 px-4">
                La friction réelle n'est souvent pas là où on l'imagine.
              </p>

              {/* Micro phrase avant CTA */}
              <p className="text-center text-gray-600 text-sm mb-4">
                Découvrez en quelques minutes ce qui ralentit réellement votre équipe.
              </p>

              {/* CTA DOMINANT */}
              <div className="mb-5">
                <Button 
                  onClick={() => setShowIntro(true)}
                  size="lg"
                  className="w-full text-white py-7 text-xl font-bold shadow-xl hover:shadow-2xl transition-all border-0 rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, #e62c86 0%, #f3a114 100%)'
                  }}
                >
                  Identifier les frictions de mon équipe
                  <ArrowRight className="w-6 h-6 ml-2" />
                </Button>
              </div>

              {/* PREUVES RAPIDES */}
              <div className="flex justify-center gap-5 mb-5">
                <span className="flex items-center gap-1.5 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#52a385]" />
                  3 min
                </span>
                <span className="flex items-center gap-1.5 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#52a385]" />
                  15 questions
                </span>
                <span className="flex items-center gap-1.5 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#52a385]" />
                  résultat immédiat
                </span>
              </div>

              {/* DISCLAIMER */}
              <p className="text-center text-xs text-gray-400">
                Ce diagnostic analyse les mécanismes opérationnels, pas les personnalités.
              </p>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-gray-500 text-xs">WEDO Coaching & Conseil</p>
            <button 
              onClick={onAdmin}
              data-testid="admin-link"
              className="text-gray-600 text-xs mt-2 hover:text-gray-400 transition-colors"
            >
              Administration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Page 2 - Intro avec Radar
  return (
    <div className="min-h-screen bg-[#0f1c47] flex items-center justify-center p-4">
      <div className="max-w-lg mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_manager-inventory/artifacts/kqdhn43c_2024_10_make_it_matter.png" 
            alt="WEDO" 
            className="mx-auto h-14 object-contain"
          />
        </div>

        <Card className="bg-white border-0 shadow-2xl">
          <CardContent className="p-8">
            
            {/* Titre - Plus stratégique */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#0f1c47]">
                Votre diagnostic en 5 dimensions
              </h2>
            </div>
            
            {/* Radar Preview - Plus grand */}
            <div className="mb-4">
              <RadarPreview />
            </div>
            
            {/* Texte sous radar */}
            <p className="text-center text-gray-500 text-sm mb-6">
              Chaque dimension sera évaluée pour identifier les zones de friction dans votre équipe.
            </p>
            
            {/* Dimensions - Noms simplifiés, 2 colonnes */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50">
                <div className="w-3 h-3 rounded-full bg-[#007BFF]"></div>
                <span className="text-gray-700 text-sm font-medium">Priorités</span>
              </div>
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50">
                <div className="w-3 h-3 rounded-full bg-[#8E44AD]"></div>
                <span className="text-gray-700 text-sm font-medium">Décision</span>
              </div>
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50">
                <div className="w-3 h-3 rounded-full bg-[#FF8C00]"></div>
                <span className="text-gray-700 text-sm font-medium">Responsabilisation</span>
              </div>
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50">
                <div className="w-3 h-3 rounded-full bg-[#52a385]"></div>
                <span className="text-gray-700 text-sm font-medium">Communication</span>
              </div>
              <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50 col-span-2 justify-center">
                <div className="w-3 h-3 rounded-full bg-[#e62c86]"></div>
                <span className="text-gray-700 text-sm font-medium">Exécution</span>
              </div>
            </div>

            {/* Mini features - Textes améliorés */}
            <div className="flex justify-center gap-4 text-xs text-gray-500 mb-6">
              <div className="flex flex-col items-center gap-1">
                <Clock className="w-5 h-5 text-[#e62c86]" />
                <span className="font-medium">3 min</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Gauge className="w-5 h-5 text-[#52a385]" />
                <span className="font-medium">Diagnostic opérationnel</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <TrendingUp className="w-5 h-5 text-[#f3a114]" />
                <span className="font-medium">Lecture stratégique</span>
              </div>
            </div>

            {/* Phrase avant CTA */}
            <p className="text-center text-gray-600 text-sm mb-4">
              15 questions rapides pour analyser la dynamique de votre équipe.
            </p>

            {/* CTA */}
            <Button 
              onClick={onStart}
              size="lg"
              className="w-full text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all border-0 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, #52a385 0%, #0f1c47 100%)'
              }}
            >
              Commencer le diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WelcomeScreen;
